import type { Db } from "./client";
import { getUserId } from "./client";
import { throwIfPgError } from "./errors";
import type {
  PhotoTagRow,
  PhotoTagInsert,
  PhotoTagSubjectType,
  PhotoRow,
} from "@garden/types/db";

export async function createPhotoTag(
  client: Db,
  input: {
    photo_id: string;
    subject_type: PhotoTagSubjectType;
    subject_id: string;
    is_primary?: boolean;
  },
): Promise<PhotoTagRow> {
  const userId = await getUserId(client);
  const row: PhotoTagInsert = {
    user_id: userId,
    photo_id: input.photo_id,
    subject_type: input.subject_type,
    subject_id: input.subject_id,
    is_primary: input.is_primary ?? false,
  };
  const { data, error } = await client
    .from("photo_tag")
    .insert(row as PhotoTagInsert) // `as` cast sanctioned at Supabase boundary per spec §5
    .select("*")
    .single();
  throwIfPgError(error);
  return data as PhotoTagRow; // `as` cast sanctioned at Supabase boundary per spec §5
}

/** Returns the photo rows currently tagged to a given subject (plant or
 *  location), most-recently-taken first. Joins photo_tag → photo. */
export async function listPhotosForSubject(
  client: Db,
  subjectType: PhotoTagSubjectType,
  subjectId: string,
): Promise<PhotoRow[]> {
  const { data, error } = await client
    .from("photo_tag")
    .select("photo:photo_id(*)")
    .eq("subject_type", subjectType)
    .eq("subject_id", subjectId);
  throwIfPgError(error);
  // Supabase resolves the foreign-key embed to either an object or null per row.
  type Joined = { photo: PhotoRow | null };
  const rows = ((data ?? []) as unknown as Joined[]) // `as` cast sanctioned at Supabase boundary per spec §5
    .map((r) => r.photo)
    .filter((p): p is PhotoRow => p !== null);
  rows.sort((a, b) => {
    const at = a.taken_date ?? a.uploaded_at;
    const bt = b.taken_date ?? b.uploaded_at;
    return at < bt ? 1 : at > bt ? -1 : 0;
  });
  return rows;
}

/** Plant ids that this user has tagged most recently (max `limit`).
 *  Used to populate the suggestion list on /tag. */
export async function listRecentlyTaggedPlantIds(
  client: Db,
  limit = 5,
  gardenId?: string,
): Promise<string[]> {
  let q = client
    .from("photo_tag")
    .select("subject_id, created_at")
    .eq("subject_type", "plant");
  if (gardenId) q = q.eq("garden_id", gardenId);
  const { data, error } = await q
    .order("created_at", { ascending: false })
    .limit(limit * 5); // pull a few extra so de-dup gives us `limit` distinct ids
  throwIfPgError(error);
  type Row = { subject_id: string; created_at: string };
  const rows = (data ?? []) as Row[];
  const seen = new Set<string>();
  const out: string[] = [];
  for (const r of rows) {
    if (seen.has(r.subject_id)) continue;
    seen.add(r.subject_id);
    out.push(r.subject_id);
    if (out.length >= limit) break;
  }
  return out;
}

/** Does this plant already have any photos tagged to it? Used to decide
 *  whether the next tag should be marked `is_primary=true`. */
export async function plantHasAnyPhoto(
  client: Db,
  plantId: string,
): Promise<boolean> {
  const { count, error } = await client
    .from("photo_tag")
    .select("id", { head: true, count: "exact" })
    .eq("subject_type", "plant")
    .eq("subject_id", plantId);
  throwIfPgError(error);
  return (count ?? 0) > 0;
}
